import {
    Checkbox,
    Collapse,
    Dropdown,
    Icon,
    Input,
    Menu,
    Popover,
    Radio,
    Select,
} from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { RadioChangeEvent } from "antd/es/radio/interface";
import React from "react";
import { SketchPicker } from "react-color";

import { hasFeature } from "../../../../Actions/Actions";
import {
    CollaborateOptions,
    CommunityMatchActivityColumn,
    CommunityMatchContactColumn,
    CompanyType,
    GeographyBorderThickness,
    GeographyType,
    GeographyTypeMeasurement,
    MatchArea,
    MatchView,
    PrintFileTypeOutput,
    PrintLegend,
    TerritoryDrawMode,
    ColorOptions,
} from "../../../../Enums/Enums";
import { IUser } from "../../../../Models/IUser";
import { IStore, IUserPreferences } from "../../../../Models/Models";
import { Store } from "../../../../Models/IStore";
import { SearchArea } from "../../../../Enums/SearchArea";
import { getTranslation } from "../../../../Actions/VerbiageActions";
import { AppArea } from "../../../../Enums/AppArea";
import { ScoutFeature } from "../../../../Enums/ScoutFeature";

import DefaultLoginApp, { radioDefaultAppValues } from "./DefaultLoginApp";

interface LabeledValue {
    key: string;
    label: React.ReactNode;
}
declare type SelectValue =
    | string
    | string[]
    | number
    | number[]
    | LabeledValue
    | LabeledValue[];

interface IUserPreferencesProps {
    user: IUser;
    userPreferences: IUserPreferences;
    communitySites: Array<IStore>;

    getUserPreferences: () => void;
    getAllCommunitySites: () => void;
    updateUserPreferences: (userPreferences: IUserPreferences) => void;
}

interface IColor {
    r: number; // red
    g: number; // green
    b: number; // blue
    a: number; // opacity
};

interface IUserPreferencesState {
    activityColumnPickerVisible: boolean;
    contactColumnPickerVisible: boolean;
    sizeValue: number;
    sizeIsValid: boolean;
    chosenColorHex: string;
    chosenActualColorHex: string;
    chosenColorRGBA: IColor;
    chosenActualColorRGBA: IColor;
    loadingUserPreferences: boolean;
    defaultApp: string;
}

class UserPreferences extends React.Component<
    IUserPreferencesProps,
    IUserPreferencesState
> {
    private timeout = null;
    private _defaultOpacity = 0.3;
    /**
     *
     */
    constructor(props: IUserPreferencesProps) {
        super(props);

        this.state = {
            activityColumnPickerVisible: false,
            contactColumnPickerVisible: false,
            sizeValue: 10,
            sizeIsValid: true,
            chosenColorHex: "#00ace6",
            chosenColorRGBA: {
                r: 0,
                g: 172,
                b: 230,
                a: this._defaultOpacity,
            },
            chosenActualColorHex: "#00ace6",
            chosenActualColorRGBA: {
                r: 0,
                g: 172,
                b: 230,
                a: this._defaultOpacity,
            },
            loadingUserPreferences: false,
            defaultApp: "",
        };
    }

    public componentDidMount(): void {
        this.props.getUserPreferences();
        if (this.props.user.company.companyType === CompanyType.COMMUNITY) {
            this.props.getAllCommunitySites();
        }
    }

    public componentWillReceiveProps(nextProps: IUserPreferencesProps): void {
        if (this.state.loadingUserPreferences) {
            this.setState({
                loadingUserPreferences: false,
            });
        }

        if (nextProps.userPreferences) {
            const hex: string = nextProps.userPreferences.defaultColor;
            if (hex) {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                const rgba = {
                    r: r,
                    g: g,
                    b: b,
                    a: nextProps.userPreferences.defaultOpacity || this._defaultOpacity,
                };

                this.setState({
                    chosenColorRGBA: rgba,
                });
            }
            const actualHex: string = nextProps.userPreferences.defaultActualColor;
            if (actualHex) {
                const r = parseInt(actualHex.slice(1, 3), 16);
                const g = parseInt(actualHex.slice(3, 5), 16);
                const b = parseInt(actualHex.slice(5, 7), 16);
                const rgba = {
                    r: r,
                    g: g,
                    b: b,
                    a:
                        nextProps.userPreferences.defaultActualOpacity
                        || this._defaultOpacity,
                };

                this.setState({
                    chosenActualColorRGBA: rgba,
                });
            }
            const nextPropsDefaultLoginApp = nextProps.userPreferences.defaultLoginApp;
            if (nextPropsDefaultLoginApp && radioDefaultAppValues.indexOf(nextPropsDefaultLoginApp) >= 0) {
                this.setState({
                    defaultApp: nextPropsDefaultLoginApp,
                });
            } else {
                // If null/doesn't exist in radio list then default to SCOUT
                const fallbackAppName = hasFeature(this.props.user, ScoutFeature.Scout4)
                    ? "SCOUT"
                    : "PRODUCT_SELECTION";
                this.setState({
                    defaultApp: fallbackAppName,
                });
            }
        }
    }

    public render(): JSX.Element {
        if (this.props.userPreferences) {
            let defaultSearchArea: SearchArea = null;
            if (this.props.userPreferences.searchAreaDefaultInternet) {
                defaultSearchArea = SearchArea.INTERNET;
            } else if (this.props.userPreferences.searchAreaDefaultFiles) {
                defaultSearchArea = SearchArea.FILES;
            } else if (this.props.userPreferences.searchAreaDefaultLocations) {
                defaultSearchArea = SearchArea.LOCATIONS;
            } else if (this.props.userPreferences.searchAreaDefaultPushpins) {
                defaultSearchArea = SearchArea.PUSHPINS;
            } else if (this.props.userPreferences.searchAreaDefaultTerritories) {
                defaultSearchArea = SearchArea.TERRITORIES;
            }
            /* 11714, 11914 */
            if (defaultSearchArea === null) {
                if (
                    this.props.user?.company
                    && this.props.user.company.companyType
                    === CompanyType.COMMERCIAL_REAL_ESTATE
                ) {
                    defaultSearchArea = SearchArea.INTERNET;
                } else {
                    defaultSearchArea = SearchArea.LOCATIONS;
                }
            }

            let defaultPrintType: PrintFileTypeOutput = PrintFileTypeOutput.PDF;
            if (this.props.userPreferences.printFileTypeOutputIsPng) {
                defaultPrintType = PrintFileTypeOutput.PNG;
            }

            let defaultActiveItemsLegendSetting: PrintLegend = PrintLegend.OFF;
            if (this.props.userPreferences.printDisplayActiveItemsLegend) {
                defaultActiveItemsLegendSetting = PrintLegend.ON;
            }

            let defaultMatchesView: MatchView = MatchView.GRID;
            if (this.props.userPreferences.defaultMatchViewIsList) {
                defaultMatchesView = MatchView.LIST;
            }

            let defaultMatchesTab: MatchArea = MatchArea.CONTACTS;
            if (this.props.userPreferences.defaultMatchAreaIsMyActivity) {
                defaultMatchesTab = MatchArea.MY_ACTIVITY;
            } else if (this.props.userPreferences.defaultMatchAreaIsFiles) {
                defaultMatchesTab = MatchArea.FILES;
            }

            let defaultTerritoryMode: TerritoryDrawMode = TerritoryDrawMode.FREE_DRAW;
            if (this.props.userPreferences.defaultTerritoryDrawModeIsGeography) {
                defaultTerritoryMode = TerritoryDrawMode.GEOGRAPHY;
            }

            let defaultUnitType: GeographyType = GeographyType.DRIVE_TIME;
            if (this.props.userPreferences.defaultToRing) {
                defaultUnitType = GeographyType.RING;
            }

            let defaultMeasurementType: GeographyTypeMeasurement
                = GeographyTypeMeasurement.MILE;
            if (this.props.userPreferences.measurementTypeIsKilometer) {
                defaultMeasurementType = GeographyTypeMeasurement.KILOMETER;
            }

            let defaultColorType: ColorOptions = ColorOptions.AUTO;
            if (!this.props.userPreferences.autoColorByDefault) {
                defaultColorType = ColorOptions.CUSTOM;
            }

            let defaultActualColorType: ColorOptions = ColorOptions.AUTO;
            if (
                this.props.userPreferences.defaultActualColor
                && !this.props.userPreferences.autoColorActualByDefault
            ) {
                defaultActualColorType = ColorOptions.CUSTOM;
            }

            let defaultCommunitySite: IStore = null;
            if (
                this.props.userPreferences.defaultSite
                && this.props.userPreferences.defaultSite.id !== 0
            ) {
                defaultCommunitySite = this.props.userPreferences.defaultSite;
            }

            // user has not set any preferences, set a default border thickness
            let defaultBorderThickness = true;
            if (
                !this.props.userPreferences.defaultBorderThicknessXThin
                && !this.props.userPreferences.defaultBorderThicknessThin
                && !this.props.userPreferences.defaultBorderThicknessMedium
                && !this.props.userPreferences.defaultBorderThicknessThick
            ) {
                defaultBorderThickness = false;
            }
            // user has not set any preferences, set a default actual border thickness
            let defaultActualBorderThickness = true;
            if (
                !this.props.userPreferences.defaultActualBorderThicknessXThin
                && !this.props.userPreferences.defaultActualBorderThicknessThin
                && !this.props.userPreferences.defaultActualBorderThicknessMedium
                && !this.props.userPreferences.defaultActualBorderThicknessThick
            ) {
                defaultActualBorderThickness = false;
            }

            const activityColumnMenu: JSX.Element = (
                <Menu>
                    <Menu.Item className="column-option">
                        Name
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.activityColumnNameDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.NAME,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Date Created
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences
                                    .activityColumnDateCreatedDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.DATE_CREATED,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Activity Result
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences
                                    .activityColumnActivityResultDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.ACTIVITY_RESULT,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Next Step
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.activityColumnNextStepDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.NEXT_STEP,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Notes
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.activityColumnNotesDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.NOTES,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Created By
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.activityColumnCreatedByDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.CREATED_BY,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Due Date
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.activityColumnDueDateDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.DUE_DATE,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Rating
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.activityColumnRatingDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activityColumnDefaultsChange(
                                    CommunityMatchActivityColumn.RATING,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                </Menu>
            );

            const contactColumnMenu: JSX.Element = (
                <Menu>
                    <Menu.Item className="column-option">
                        Name
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnNameDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.NAME,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Title
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnTitleDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.TITLE,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Office Phone
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences
                                    .contactColumnOfficePhoneDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.OFFICE_PHONE,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Cell Phone
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnCellPhoneDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.CELL_PHONE,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Email
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences
                                    .contactColumnEmailAddressDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.EMAIL_ADDRESS,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Added By
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnAddedByDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.ADDED_BY,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Address
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnAddressDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.ADDRESS,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Linkedin
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnLinkedinDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.LINKEDIN,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Region
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnRegionDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.REGION,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Time Zone
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnTimeZoneDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.TIME_ZONE,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                    <Menu.Item className="column-option">
                        Notes
                        <Checkbox
                            style={{ marginLeft: "1rem" }}
                            defaultChecked={
                                this.props.userPreferences.contactColumnNotesDefaultVisible
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.contactColumnDefaultsChange(
                                    CommunityMatchContactColumn.NOTES,
                                    event
                                )
                            }
                        ></Checkbox>
                    </Menu.Item>
                </Menu>
            );

            const customColorPopover: JSX.Element = !this.props.userPreferences
                .autoColorByDefault
                ? <Popover
                    placement="right"
                    title={null}
                    trigger="click"
                    content={
                        <SketchPicker
                            onChange={this.defaultColorChange}
                            color={this.state.chosenColorRGBA}
                        />
                    }
                >
                    <div
                        className="square"
                        style={{
                            backgroundColor:
                                this.props.userPreferences.defaultColor || "#00ace6",
                            borderColor: this.props.userPreferences.defaultColor || "#00ace6",
                            opacity:
                                this.props.userPreferences.defaultOpacity
                                || this._defaultOpacity,
                            display: "inline-block",
                            marginBottom: "-5px",
                        }}
                    ></div>
                </Popover>
                : null;

            const customActualColorPopover: JSX.Element
                = defaultActualColorType === ColorOptions.CUSTOM ? (
                    <Popover
                        placement="right"
                        title={null}
                        trigger="click"
                        content={
                            <SketchPicker
                                onChange={this.defaultActualColorChange}
                                color={this.state.chosenActualColorRGBA}
                            />
                        }
                    >
                        <div
                            className="square"
                            style={{
                                backgroundColor:
                                    this.props.userPreferences.defaultActualColor || "#00ace6",
                                borderColor:
                                    this.props.userPreferences.defaultActualColor || "#00ace6",
                                opacity:
                                    this.props.userPreferences.defaultActualOpacity
                                    || this._defaultOpacity,
                                display: "inline-block",
                                marginBottom: "-5px",
                            }}
                        ></div>
                    </Popover>
                ) : null;

            const actualDRRingDetails: JSX.Element = (
                <div className="body">
                    <div className="label">COLOR</div>
                    <div>
                        <Radio.Group
                            value={defaultActualColorType}
                            onChange={(event: RadioChangeEvent): void =>
                                this.defaultActualColorOptionChange(event)
                            }
                        >
                            <Radio
                                value={ColorOptions.AUTO}
                                onChange={(event: CheckboxChangeEvent): void =>
                                    this.defaultActualColorChange(event)
                                }
                                style={{ display: "block" }}
                            >
                                Auto Color
                            </Radio>
                            <Radio value={ColorOptions.CUSTOM}>Custom Color</Radio>
                            {customActualColorPopover}
                        </Radio.Group>
                    </div>
                    <div className="label">BORDER THICKNESS</div>
                    <div className="row">
                        <div
                            className="square"
                            style={{
                                marginRight: ".5rem",
                                borderColor:
                                    this.props.userPreferences.defaultActualColor
                                    || this.state.chosenActualColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenActualColorRGBA
                                    ? this.state.chosenActualColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor: this.props.userPreferences
                                    .defaultActualBorderThicknessXThin
                                    ? this.getActualRgbaColor()
                                    : null,
                            }}
                            onClick={(): void =>
                                this.defaultActualBorderThicknessChange(
                                    GeographyBorderThickness.XTHIN
                                )
                            }
                        ></div>
                        <div
                            className="square"
                            style={{
                                marginRight: ".5rem",
                                borderColor:
                                    this.props.userPreferences.defaultActualColor
                                    || this.state.chosenActualColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenActualColorRGBA
                                    ? this.state.chosenActualColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor:
                                    this.props.userPreferences.defaultActualBorderThicknessThin
                                        || !defaultActualBorderThickness
                                        ? this.getActualRgbaColor()
                                        : null,
                            }}
                            onClick={(): void =>
                                this.defaultActualBorderThicknessChange(
                                    GeographyBorderThickness.THIN
                                )
                            }
                        ></div>
                        <div
                            className="square"
                            style={{
                                marginRight: ".5rem",
                                borderColor:
                                    this.props.userPreferences.defaultActualColor
                                    || this.state.chosenActualColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenActualColorRGBA
                                    ? this.state.chosenActualColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor: this.props.userPreferences
                                    .defaultActualBorderThicknessMedium
                                    ? this.getActualRgbaColor()
                                    : null,
                            }}
                            onClick={(): void =>
                                this.defaultActualBorderThicknessChange(
                                    GeographyBorderThickness.MEDIUM
                                )
                            }
                        ></div>
                        <div
                            className="square"
                            style={{
                                borderColor:
                                    this.props.userPreferences.defaultActualColor
                                    || this.state.chosenActualColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenActualColorRGBA
                                    ? this.state.chosenActualColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor: this.props.userPreferences
                                    .defaultActualBorderThicknessThick
                                    ? this.getActualRgbaColor()
                                    : null,
                            }}
                            onClick={(): void =>
                                this.defaultActualBorderThicknessChange(
                                    GeographyBorderThickness.THICK
                                )
                            }
                        ></div>
                    </div>
                    <div className="label">EXPLORE</div>
                    <div style={{ marginTop: "1rem" }}>
                        <Checkbox
                            defaultChecked={
                                this.props.userPreferences.activateActualDTByDefault
                            }
                            onChange={(event: CheckboxChangeEvent): void =>
                                this.activateActualDTChange(event)
                            }
                        >
                            Turn on Actual Drive Time / Trade Area when I explore a location
                        </Checkbox>
                    </div>
                </div>
            );

            const mapOptionsDetails: JSX.Element = (
                <div className="body">
                    <div className="label">SIZE</div>
                    {/* <Radio.Group
                        onChange={(event: RadioChangeEvent): void => this.defaultSizeChange(event)}>
                        <Radio
                            style={{ display: "block" }}
                            value={"10"}
                            checked={!this.state.sizeIsCustom && this.props.userPreferences.defaultSize === "10"}>10</Radio>
                        <Radio
                            style={{ display: "block" }}
                            value="custom"
                            checked={this.state.sizeIsCustom || this.props.userPreferences.defaultSize !== "10"}>
                        </Radio>
                    </Radio.Group> */}

                    <Input
                        id="user-preferences-custom-size-input"
                        style={{ maxWidth: "250px" }}
                        className={!this.state.sizeIsValid ? "invalid" : null}
                        placeholder="Enter a custom size: 10, 15, 20"
                        onChange={(value: React.ChangeEvent): void =>
                            this.defaultSizeChange(value)
                        }
                        defaultValue={this.props.userPreferences.defaultSize}
                    ></Input>

                    <div className="label">UNIT TYPE</div>
                    <Radio.Group
                        value={defaultUnitType}
                        onChange={(event: RadioChangeEvent): void =>
                            this.defaultUnitTypeChange(event)
                        }
                    >
                        <Radio
                            style={{ display: "block" }}
                            value={GeographyType.DRIVE_TIME}
                        >
                            Drive Time
                        </Radio>
                        <Radio style={{ display: "block" }} value={GeographyType.RING}>
                            Ring
                        </Radio>
                    </Radio.Group>

                    <div className="label">MEASUREMENT</div>
                    <Radio.Group
                        value={defaultMeasurementType}
                        disabled={defaultUnitType === GeographyType.DRIVE_TIME}
                        onChange={(event: RadioChangeEvent): void =>
                            this.defaultMeasurementTypeChange(event)
                        }
                    >
                        <Radio
                            style={{ display: "block" }}
                            value={GeographyTypeMeasurement.MILE}
                        >
                            Mile
                        </Radio>
                        <Radio
                            style={{ display: "block" }}
                            value={GeographyTypeMeasurement.KILOMETER}
                        >
                            Kilometer
                        </Radio>
                    </Radio.Group>

                    <div className="label">COLOR</div>
                    <div>
                        <Radio.Group
                            value={defaultColorType}
                            onChange={(event: RadioChangeEvent): void =>
                                this.defaultColorOptionChange(event)
                            }
                        >
                            <Radio
                                value={ColorOptions.AUTO}
                                onChange={(event: RadioChangeEvent): void =>
                                    this.defaultColorChange(event)
                                }
                                style={{ display: "block" }}
                            >
                                Auto Color
                            </Radio>
                            <Radio value={ColorOptions.CUSTOM}>Custom Color</Radio>
                            {customColorPopover}
                        </Radio.Group>
                    </div>

                    <div className="label">BORDER THICKNESS</div>
                    <div className="row">
                        <div
                            className="square"
                            style={{
                                marginRight: ".5rem",
                                borderColor:
                                    this.props.userPreferences.defaultColor
                                    || this.state.chosenColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenColorRGBA
                                    ? this.state.chosenColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor: this.props.userPreferences
                                    .defaultBorderThicknessXThin
                                    ? this.getRgbaColor()
                                    : null,
                            }}
                            onClick={(): void =>
                                this.defaultBorderThicknessChange(
                                    GeographyBorderThickness.XTHIN
                                )
                            }
                        ></div>
                        <div
                            className="square"
                            style={{
                                marginRight: ".5rem",
                                borderColor:
                                    this.props.userPreferences.defaultColor
                                    || this.state.chosenColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenColorRGBA
                                    ? this.state.chosenColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor:
                                    this.props.userPreferences.defaultBorderThicknessThin
                                        || !defaultBorderThickness
                                        ? this.getRgbaColor()
                                        : null,
                            }}
                            onClick={(): void =>
                                this.defaultBorderThicknessChange(GeographyBorderThickness.THIN)
                            }
                        ></div>
                        <div
                            className="square"
                            style={{
                                marginRight: ".5rem",
                                borderColor:
                                    this.props.userPreferences.defaultColor
                                    || this.state.chosenColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenColorRGBA
                                    ? this.state.chosenColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor: this.props.userPreferences
                                    .defaultBorderThicknessMedium
                                    ? this.getRgbaColor()
                                    : null,
                            }}
                            onClick={(): void =>
                                this.defaultBorderThicknessChange(
                                    GeographyBorderThickness.MEDIUM
                                )
                            }
                        ></div>
                        <div
                            className="square"
                            style={{
                                borderColor:
                                    this.props.userPreferences.defaultColor
                                    || this.state.chosenColorHex
                                    || "#00ace6",
                                opacity: this.state.chosenColorRGBA
                                    ? this.state.chosenColorRGBA.a
                                    : this._defaultOpacity,
                                backgroundColor: this.props.userPreferences
                                    .defaultBorderThicknessThick
                                    ? this.getRgbaColor()
                                    : null,
                            }}
                            onClick={(): void =>
                                this.defaultBorderThicknessChange(
                                    GeographyBorderThickness.THICK
                                )
                            }
                        ></div>
                    </div>
                    {hasFeature(this.props.user, ScoutFeature.Reports) && (
                        <>
                            <div className="label">REPORTING</div>
                            <div style={{ marginTop: "1rem" }}>
                                <Checkbox
                                    defaultChecked={
                                        this.props.userPreferences.addToStudyAreaByDefault
                                    }
                                    onChange={(event: CheckboxChangeEvent): void =>
                                        this.defaultAddToStudyAreaChange(event)
                                    }
                                >
                                    Automatically add to Study Area
                                </Checkbox>
                            </div>
                        </>
                    )}
                </div>
            );

            const territoryOptionDetails: JSX.Element = (
                <div className="body">
                    <div className="label">MODE</div>
                    <Radio.Group
                        value={defaultTerritoryMode}
                        onChange={(event: RadioChangeEvent): void =>
                            this.defaultTerritoryDrawModeChange(event)
                        }
                    >
                        <Radio
                            style={{ display: "block" }}
                            value={TerritoryDrawMode.FREE_DRAW}
                        >
                            Free Draw
                        </Radio>
                        <Radio
                            style={{ display: "block" }}
                            value={TerritoryDrawMode.GEOGRAPHY}
                        >
                            Geography
                        </Radio>
                    </Radio.Group>
                </div>
            );

            const territoryWord = getTranslation(
                this.props.user.company.companyType,
                AppArea.TERRITORIES,
                false
            );
            const territoryWordPlural = getTranslation(
                this.props.user.company.companyType,
                AppArea.TERRITORIES,
                true
            );
            const territoryOptionsKey = `${territoryWord} Creation`;

            const territoryOptions: JSX.Element = hasFeature(this.props.user, ScoutFeature.Territories)
                && <Collapse bordered={false}>
                    <Collapse.Panel
                        header={territoryOptionsKey}
                        key={territoryOptionsKey}
                    >
                        {territoryOptionDetails}
                    </Collapse.Panel>
                </Collapse>
                ;

            const mapOptions: JSX.Element = (
                <Collapse bordered={false}>
                    <Collapse.Panel header="Drive Time / Ring" key="Drive Time / Ring">
                        {mapOptionsDetails}
                    </Collapse.Panel>
                </Collapse>
            );

            const actualDTRingSection: JSX.Element = hasFeature(this.props.user, ScoutFeature.StoreExplore)
                ? <Collapse bordered={false}>
                    <Collapse.Panel
                        header="Actual Drive Time / Trade Area"
                        key="Actual Drive Time / Trade Area"
                    >
                        {actualDRRingDetails}
                    </Collapse.Panel>
                </Collapse>
                : null;

            const mapActions: JSX.Element = hasFeature(this.props.user, ScoutFeature.Scout4) && (
                <div className="option" style={{ borderBottom: "none" }}>
                    <div className="header" style={{ borderBottom: "1px solid #d9d9d9" }}>
                        Map Actions
                    </div>
                    {mapOptions}
                    {actualDTRingSection}
                    {territoryOptions}
                </div>
            );

            const printOptions: JSX.Element = hasFeature(this.props.user, ScoutFeature.Toolbox)
                && hasFeature(this.props.user, ScoutFeature.Scout4)
                && <div className="option">
                    <div className="header">Print Options</div>
                    <div className="item">
                        File Type
                        <Select
                            className="pull-right select"
                            defaultValue={defaultPrintType}
                            onChange={(value: SelectValue): void =>
                                this.printModeChanged(value)
                            }
                        >
                            <Select.Option value={PrintFileTypeOutput.PDF}>PDF</Select.Option>
                            <Select.Option value={PrintFileTypeOutput.PNG}>PNG</Select.Option>
                        </Select>
                    </div>
                    <div className="item">
                        Display Active Items Legend
                        <Select
                            className="pull-right select"
                            defaultValue={defaultActiveItemsLegendSetting}
                            onChange={(value: SelectValue): void =>
                                this.printActiveItemsLegendChanged(value)
                            }
                        >
                            <Select.Option value={PrintLegend.ON}> On</Select.Option>
                            <Select.Option value={PrintLegend.OFF}>Off</Select.Option>
                        </Select>
                    </div>
                </div>;

            const searchOptions: JSX.Element = hasFeature(this.props.user, ScoutFeature.Scout4) && (
                <div className="option">
                    <div className="header">Search Options</div>
                    <Collapse bordered={false}>
                        <Collapse.Panel header="Search Options" key="Search Options">
                            <div className="body">
                                <div className="label">DEFAULT AREA</div>
                                <Radio.Group
                                    value={defaultSearchArea}
                                    onChange={(event: RadioChangeEvent): void =>
                                        this.defaultSearchAreaChangeHandler(event)
                                    }
                                >
                                    <Radio
                                        style={{ display: "block" }}
                                        value={SearchArea.INTERNET}
                                    >
                                        Internet
                                    </Radio>
                                    <Radio style={{ display: "block" }} value={SearchArea.FILES}>
                                        Files
                                    </Radio>
                                    <Radio
                                        style={{ display: "block" }}
                                        value={SearchArea.LOCATIONS}
                                    >
                                        {getTranslation(
                                            this.props.user.company.companyType,
                                            AppArea.LOCATIONS,
                                            true
                                        )}
                                    </Radio>
                                    <Radio
                                        style={{ display: "block" }}
                                        value={SearchArea.PUSHPINS}
                                    >
                                        Pushpins
                                    </Radio>
                                    <Radio
                                        style={{ display: "block" }}
                                        value={SearchArea.TERRITORIES}
                                    >
                                        {territoryWordPlural}
                                    </Radio>
                                </Radio.Group>
                            </div>
                        </Collapse.Panel>
                    </Collapse>
                </div>
            );

            let communityOptions: JSX.Element | null = null;
            if (
                this.props.user.company.companyType === CompanyType.COMMUNITY
                && hasFeature(this.props.user, ScoutFeature.RetailMatch)
                && hasFeature(this.props.user, ScoutFeature.Scout4)
            ) {
                let communitySites: Array<JSX.Element> = null;
                communitySites = this.props.communitySites.map((store: IStore) => {
                    return (
                        <Select.Option value={store.id} key={store.id.toString()}>
                            {store.name}
                        </Select.Option>
                    );
                });

                communityOptions = (
                    <div className="option">
                        <div className="header">Matches</div>
                        <div className="item">
                            Default Site
                            <Select
                                className="pull-right select"
                                style={{ width: "250px" }}
                                defaultValue={
                                    defaultCommunitySite ? defaultCommunitySite.name : null
                                }
                                onChange={(value: SelectValue): void =>
                                    this.defaultSiteChange(value)
                                }
                                placeholder="Select from your Sites"
                            >
                                {communitySites}
                            </Select>
                        </div>
                        <div className="item">
                            Contacts Column Picker
                            <Dropdown
                                className="pull-right select"
                                overlay={contactColumnMenu}
                                trigger={["click"]}
                                placement="topCenter"
                                onVisibleChange={this.setContactColumnPickerVisiblity}
                                visible={this.state.contactColumnPickerVisible}
                            >
                                <span>
                                    Choose Defaults <Icon type="down"></Icon>
                                </span>
                            </Dropdown>
                        </div>
                        <div className="item">
                            Activity Column Picker
                            <Dropdown
                                className="pull-right"
                                overlay={activityColumnMenu}
                                trigger={["click"]}
                                placement="topCenter"
                                onVisibleChange={this.setActivityColumnPickerVisiblity}
                                visible={this.state.activityColumnPickerVisible}
                            >
                                <span>
                                    Choose Defaults <Icon type="down"></Icon>
                                </span>
                            </Dropdown>
                        </div>
                        <div className="item">
                            Default View
                            <Select
                                className="pull-right select"
                                defaultValue={defaultMatchesView}
                                onChange={(value: SelectValue): void =>
                                    this.defaultMatchesViewChange(value)
                                }
                            >
                                <Select.Option value={MatchView.GRID}>Grid</Select.Option>
                                <Select.Option value={MatchView.LIST}>List</Select.Option>
                            </Select>
                        </div>
                        <div className="item">
                            Default Tab
                            <Select
                                className="pull-right select"
                                defaultValue={defaultMatchesTab}
                                onChange={(value: SelectValue): void =>
                                    this.defaultMatchesAreaChange(value)
                                }
                            >
                                <Select.Option value={MatchArea.CONTACTS}>
                                    Contacts
                                </Select.Option>
                                <Select.Option value={MatchArea.MY_ACTIVITY}>
                                    My Activity
                                </Select.Option>
                                <Select.Option value={MatchArea.FILES}>Files</Select.Option>
                            </Select>
                        </div>
                    </div>
                );
            }

            return (
                <div className="UserPreferences">
                    <div className="section">
                        <div
                            className="header"
                            style={{ marginBottom: "1rem", backgroundColor: "initial" }}
                        >
                            Preferences
                        </div>
                        <DefaultLoginApp
                            user={this.props.user}
                            defaultApp={this.state.defaultApp}
                            defaultAppChange={this.defaultAppChange}
                        />
                        {mapActions}
                        {/* {collaborateOptions} */}
                        {printOptions}
                        {searchOptions}
                        {communityOptions}
                    </div>
                </div>
            );
        }

        return null;
    }

    private setLoadingUserPreferences(loading: boolean) {
        this.setState({
            loadingUserPreferences: loading,
        });
    }

    private colorIsValid(color: IColor): boolean {
        return color
            && color.r !== null
            && color.g !== null
            && color.b !== null
            && color.a !== null;
    };

    private getRgbaColor = (): string => {
        return this.colorIsValid(this.state.chosenColorRGBA)
            ? `rgb(${this.state.chosenColorRGBA.r}, ${this.state.chosenColorRGBA.g}, ${this.state.chosenColorRGBA.b}, ${this.state.chosenColorRGBA.a})`
            : `rgb(${0}, ${172}, ${230}, ${0.3})`;
    };

    private getActualRgbaColor = (): string => {
        return this.colorIsValid(this.state.chosenActualColorRGBA)
            ? `rgb(${this.state.chosenActualColorRGBA.r}, ${this.state.chosenActualColorRGBA.g}, ${this.state.chosenActualColorRGBA.b}, ${this.state.chosenActualColorRGBA.a})`
            : `rgb(${0}, ${172}, ${230}, ${0.3})`;
    };

    private defaultColorChange = (value): void => {
        clearTimeout(this.timeout);

        if (value) {
            this.timeout = setTimeout((): void => {
                this.setState({
                    chosenColorHex: value.hex, // example - "#bd10e0"
                    chosenColorRGBA: value.rgb, // example - rgb: {a: 0.64, b: 194, g: 227, r: 80}
                });

                const hexColorValue: string = value.hex || "#00ace6";
                const opacityValue: number = value.rgb
                    ? value.rgb.a
                    : this._defaultOpacity;

                const userPreferencesCopy: IUserPreferences = {
                    ...this.props.userPreferences,
                };

                userPreferencesCopy.defaultColor = hexColorValue;
                userPreferencesCopy.defaultOpacity = opacityValue;

                this.setLoadingUserPreferences(true);
                this.props.updateUserPreferences(userPreferencesCopy);
            }, 500);
        }
    };

    private defaultActualColorChange = (value): void => {
        clearTimeout(this.timeout);

        if (value) {
            this.timeout = setTimeout((): void => {
                this.setState({
                    chosenActualColorHex: value.hex, // example - "#bd10e0"
                    chosenActualColorRGBA: value.rgb, // example - rgb: {a: 0.64, b: 194, g: 227, r: 80}
                });

                const hexColorValue: string = value.hex || "#00ace6";
                const opacityValue: number = value.rgb
                    ? value.rgb.a
                    : this._defaultOpacity;

                const userPreferencesCopy: IUserPreferences = {
                    ...this.props.userPreferences,
                };

                userPreferencesCopy.defaultActualColor = hexColorValue;
                userPreferencesCopy.defaultActualOpacity = opacityValue;

                this.setLoadingUserPreferences(true);
                this.props.updateUserPreferences(userPreferencesCopy);
            }, 500);
        }
    };

    private defaultSizeChange = (event): void => {
        const inputValue = event.target.value; // save it for the setTimeout

        switch (typeof inputValue) {
            case "number":
                break;
            case "string":
                if (inputValue === "custom") {
                    const customInput: HTMLInputElement = document.querySelector(
                        "#user-preferences-custom-size-input"
                    );
                    customInput.value = null;
                }
                break;
        }

        const allValues: Array<string> = inputValue.split(",");

        let inputIsValid = true;
        allValues.forEach((value: string) => {
            // less than 0, greater than 180, contains letters
            if (+value <= 0 || +value >= 180 || /[a-z]/i.test(value)) {
                inputIsValid = false;
            }
        });

        this.setState({
            sizeValue: inputValue,
            sizeIsValid: inputIsValid,
        });

        clearTimeout(this.timeout);
        this.timeout = setTimeout((): void => {
            if (!inputValue || inputValue.trim().length === 0) {
                const tempValue = "10";
                const userPreferencesCopy: IUserPreferences = {
                    ...this.props.userPreferences,
                };
                userPreferencesCopy.defaultSize = tempValue;

                this.setLoadingUserPreferences(true);
                this.props.updateUserPreferences(userPreferencesCopy);
            } else {
                if (inputIsValid) {
                    const userPreferencesCopy: IUserPreferences = {
                        ...this.props.userPreferences,
                    };
                    userPreferencesCopy.defaultSize = inputValue;

                    this.setLoadingUserPreferences(true);
                    this.props.updateUserPreferences(userPreferencesCopy);
                }
            }
        }, 500);
    };

    private defaultBorderThicknessChange = (
        thickness: GeographyBorderThickness
    ): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.defaultBorderThicknessXThin = false;
        userPreferencesCopy.defaultBorderThicknessThin = false;
        userPreferencesCopy.defaultBorderThicknessMedium = false;
        userPreferencesCopy.defaultBorderThicknessThick = false;

        switch (thickness) {
            case GeographyBorderThickness.XTHIN:
                userPreferencesCopy.defaultBorderThicknessXThin = true;
                break;
            case GeographyBorderThickness.THIN:
                userPreferencesCopy.defaultBorderThicknessThin = true;
                break;
            case GeographyBorderThickness.MEDIUM:
                userPreferencesCopy.defaultBorderThicknessMedium = true;
                break;
            case GeographyBorderThickness.THICK:
                userPreferencesCopy.defaultBorderThicknessThick = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultActualBorderThicknessChange = (
        thickness: GeographyBorderThickness
    ): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.defaultActualBorderThicknessXThin = false;
        userPreferencesCopy.defaultActualBorderThicknessThin = false;
        userPreferencesCopy.defaultActualBorderThicknessMedium = false;
        userPreferencesCopy.defaultActualBorderThicknessThick = false;

        switch (thickness) {
            case GeographyBorderThickness.XTHIN:
                userPreferencesCopy.defaultActualBorderThicknessXThin = true;
                break;
            case GeographyBorderThickness.THIN:
                userPreferencesCopy.defaultActualBorderThicknessThin = true;
                break;
            case GeographyBorderThickness.MEDIUM:
                userPreferencesCopy.defaultActualBorderThicknessMedium = true;
                break;
            case GeographyBorderThickness.THICK:
                userPreferencesCopy.defaultActualBorderThicknessThick = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultSiteChange = (value): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        if (userPreferencesCopy.defaultSite === null) {
            userPreferencesCopy.defaultSite = new Store();
        }
        userPreferencesCopy.defaultSite.id = value;

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private setActivityColumnPickerVisiblity = (visible: boolean): void => {
        this.setState({
            activityColumnPickerVisible: visible,
        });
    };

    private setContactColumnPickerVisiblity = (visible: boolean): void => {
        this.setState({
            contactColumnPickerVisible: visible,
        });
    };

    private contactColumnDefaultsChange = (
        column: CommunityMatchContactColumn,
        event: CheckboxChangeEvent
    ): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        switch (column) {
            case CommunityMatchContactColumn.NAME:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnNameDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnNameDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.TITLE:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnTitleDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnTitleDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.OFFICE_PHONE:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnOfficePhoneDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnOfficePhoneDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.CELL_PHONE:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnCellPhoneDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnCellPhoneDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.EMAIL_ADDRESS:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnEmailAddressDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnEmailAddressDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.ADDED_BY:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnAddedByDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnAddedByDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.ADDRESS:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnAddressDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnAddressDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.LINKEDIN:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnLinkedinDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnLinkedinDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.REGION:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnRegionDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnRegionDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.TIME_ZONE:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnTimeZoneDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnTimeZoneDefaultVisible = false;
                }
                break;
            case CommunityMatchContactColumn.NOTES:
                if (event.target.checked) {
                    userPreferencesCopy.contactColumnNotesDefaultVisible = true;
                } else {
                    userPreferencesCopy.contactColumnNotesDefaultVisible = false;
                }
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private activityColumnDefaultsChange = (
        column: CommunityMatchActivityColumn,
        event: CheckboxChangeEvent
    ): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        switch (column) {
            case CommunityMatchActivityColumn.NAME:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnNameDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnNameDefaultVisible = false;
                }
                break;
            case CommunityMatchActivityColumn.DATE_CREATED:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnDateCreatedDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnDateCreatedDefaultVisible = false;
                }
                break;
            case CommunityMatchActivityColumn.ACTIVITY_RESULT:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnActivityResultDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnActivityResultDefaultVisible
                        = false;
                }
                break;
            case CommunityMatchActivityColumn.NEXT_STEP:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnNextStepDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnNextStepDefaultVisible = false;
                }
                break;
            case CommunityMatchActivityColumn.NOTES:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnNotesDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnNotesDefaultVisible = false;
                }
                break;
            case CommunityMatchActivityColumn.CREATED_BY:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnCreatedByDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnCreatedByDefaultVisible = false;
                }
                break;
            case CommunityMatchActivityColumn.DUE_DATE:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnDueDateDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnDueDateDefaultVisible = false;
                }
                break;
            case CommunityMatchActivityColumn.RATING:
                if (event.target.checked) {
                    userPreferencesCopy.activityColumnRatingDefaultVisible = true;
                } else {
                    userPreferencesCopy.activityColumnRatingDefaultVisible = false;
                }
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultColorOptionChange = (event: RadioChangeEvent): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        switch (event.target.value) {
            case ColorOptions.AUTO: {
                userPreferencesCopy.autoColorByDefault = true;
                break;
            }
            case ColorOptions.CUSTOM: {
                userPreferencesCopy.autoColorByDefault = false;
                break;
            }
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultActualColorOptionChange = (event: RadioChangeEvent): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        switch (event.target.value) {
            case ColorOptions.AUTO: {
                userPreferencesCopy.autoColorActualByDefault = true;
                break;
            }
            case ColorOptions.CUSTOM: {
                userPreferencesCopy.autoColorActualByDefault = false;
                break;
            }
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultAddToStudyAreaChange = (event: CheckboxChangeEvent): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        if (event.target.checked) {
            userPreferencesCopy.addToStudyAreaByDefault = true;
        } else {
            userPreferencesCopy.addToStudyAreaByDefault = false;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private activateActualDTChange = (event: CheckboxChangeEvent): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        if (event.target.checked) {
            userPreferencesCopy.activateActualDTByDefault = true;
        } else {
            userPreferencesCopy.activateActualDTByDefault = false;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultMeasurementTypeChange = (event: RadioChangeEvent): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.measurementTypeIsMile = false;
        userPreferencesCopy.measurementTypeIsKilometer = false;

        switch (event.target.value) {
            case GeographyTypeMeasurement.MILE:
                userPreferencesCopy.measurementTypeIsMile = true;
                break;
            case GeographyTypeMeasurement.KILOMETER:
                userPreferencesCopy.measurementTypeIsKilometer = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultUnitTypeChange = (event: RadioChangeEvent): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.defaultToDriveTime = false;
        userPreferencesCopy.defaultToRing = false;

        switch (event.target.value) {
            case GeographyType.DRIVE_TIME:
                userPreferencesCopy.defaultToDriveTime = true;
                break;
            case GeographyType.RING:
                userPreferencesCopy.defaultToRing = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultTerritoryDrawModeChange = (event: RadioChangeEvent): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.defaultTerritoryDrawModeIsFreeDraw = false;
        userPreferencesCopy.defaultTerritoryDrawModeIsGeography = false;

        switch (event.target.value) {
            case TerritoryDrawMode.FREE_DRAW:
                userPreferencesCopy.defaultTerritoryDrawModeIsFreeDraw = true;
                break;
            case TerritoryDrawMode.GEOGRAPHY:
                userPreferencesCopy.defaultTerritoryDrawModeIsGeography = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultMatchesAreaChange = (value: SelectValue): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.defaultMatchAreaIsContacts = false;
        userPreferencesCopy.defaultMatchAreaIsMyActivity = false;
        userPreferencesCopy.defaultMatchAreaIsFiles = false;

        switch (value) {
            case MatchArea.CONTACTS:
                userPreferencesCopy.defaultMatchAreaIsContacts = true;
                break;
            case MatchArea.MY_ACTIVITY:
                userPreferencesCopy.defaultMatchAreaIsMyActivity = true;
                break;
            case MatchArea.FILES:
                userPreferencesCopy.defaultMatchAreaIsFiles = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultMatchesViewChange = (value: SelectValue): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.defaultMatchViewIsGrid = false;
        userPreferencesCopy.defaultMatchViewIsList = false;

        switch (value) {
            case MatchView.GRID:
                userPreferencesCopy.defaultMatchViewIsGrid = true;
                break;
            case MatchView.LIST:
                userPreferencesCopy.defaultMatchViewIsList = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private printActiveItemsLegendChanged = (value: SelectValue): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        switch (value) {
            case PrintLegend.OFF:
                userPreferencesCopy.printDisplayActiveItemsLegend = false;
                break;
            case PrintLegend.ON:
                userPreferencesCopy.printDisplayActiveItemsLegend = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private defaultSearchAreaChangeHandler = (event: RadioChangeEvent) => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };
        userPreferencesCopy.searchAreaDefaultInternet = false;
        userPreferencesCopy.searchAreaDefaultFiles = false;
        userPreferencesCopy.searchAreaDefaultLocations = false;
        userPreferencesCopy.searchAreaDefaultPushpins = false;
        userPreferencesCopy.searchAreaDefaultTerritories = false;

        switch (event.target.value) {
            case SearchArea.INTERNET: {
                userPreferencesCopy.searchAreaDefaultInternet = true;
                break;
            }
            case SearchArea.FILES: {
                userPreferencesCopy.searchAreaDefaultFiles = true;
                break;
            }
            case SearchArea.LOCATIONS: {
                userPreferencesCopy.searchAreaDefaultLocations = true;
                break;
            }
            case SearchArea.PUSHPINS: {
                userPreferencesCopy.searchAreaDefaultPushpins = true;
                break;
            }
            case SearchArea.TERRITORIES: {
                userPreferencesCopy.searchAreaDefaultTerritories = true;
                break;
            }
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private printModeChanged = (value: SelectValue): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        userPreferencesCopy.printFileTypeOutputIsPdf = false;
        userPreferencesCopy.printFileTypeOutputIsPng = false;

        switch (value) {
            case PrintFileTypeOutput.PDF:
                userPreferencesCopy.printFileTypeOutputIsPdf = true;
                break;
            case PrintFileTypeOutput.PNG:
                userPreferencesCopy.printFileTypeOutputIsPng = true;
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private toggleCollaboration = (option: CollaborateOptions): void => {
        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };

        switch (option) {
            /**
             * I know we could check to see what features they actually have and then only turn those on.
             * But - doing it this way will just be easier, so that when they are given a feature,
             * their settings will already be saved. They can change as desired.
             */
            case CollaborateOptions.ALL:
                if (this.allCollaborateOptionsOn()) {
                    // turn them all off
                    userPreferencesCopy.collaborateAOIThematics = false;
                    userPreferencesCopy.collaborateLocationFilters = false;
                    userPreferencesCopy.collaboratePushpins = false;
                    userPreferencesCopy.collaborateReportHistory = false;
                    userPreferencesCopy.collaborateTerritories = false;
                    userPreferencesCopy.collaborateViewReports = false;
                    if (this.props.user.company.companyType === CompanyType.COMMUNITY) {
                        userPreferencesCopy.collaborateContactActivity = false;
                        userPreferencesCopy.collaborateContacts = false;
                        userPreferencesCopy.collaborateProspects = false;
                    }
                } else {
                    // turn them all on
                    userPreferencesCopy.collaborateAOIThematics = true;
                    userPreferencesCopy.collaborateLocationFilters = true;
                    userPreferencesCopy.collaboratePushpins = true;
                    userPreferencesCopy.collaborateReportHistory = true;
                    userPreferencesCopy.collaborateTerritories = true;
                    userPreferencesCopy.collaborateViewReports = true;
                    if (this.props.user.company.companyType === CompanyType.COMMUNITY) {
                        userPreferencesCopy.collaborateContactActivity = true;
                        userPreferencesCopy.collaborateContacts = true;
                        userPreferencesCopy.collaborateProspects = true;
                    }
                }
                break;
            case CollaborateOptions.AOI_THEMATICS:
                userPreferencesCopy.collaborateAOIThematics
                    = !userPreferencesCopy.collaborateAOIThematics;
                break;
            case CollaborateOptions.LOCATION_FILTERS:
                userPreferencesCopy.collaborateLocationFilters
                    = !userPreferencesCopy.collaborateLocationFilters;
                break;
            case CollaborateOptions.PUSHPINS:
                userPreferencesCopy.collaboratePushpins
                    = !userPreferencesCopy.collaboratePushpins;
                break;
            case CollaborateOptions.REPORT_HISTORY:
                userPreferencesCopy.collaborateReportHistory
                    = !userPreferencesCopy.collaborateReportHistory;
                break;
            case CollaborateOptions.TERRITORIES:
                userPreferencesCopy.collaborateTerritories
                    = !userPreferencesCopy.collaborateTerritories;
                break;
            case CollaborateOptions.VIEW_REPORTS:
                userPreferencesCopy.collaborateViewReports
                    = !userPreferencesCopy.collaborateViewReports;
                break;
            case CollaborateOptions.CONTACT_ACTIVITY:
                userPreferencesCopy.collaborateContactActivity
                    = !userPreferencesCopy.collaborateContactActivity;
                break;
            case CollaborateOptions.CONTACTS:
                userPreferencesCopy.collaborateContacts
                    = !userPreferencesCopy.collaborateContacts;
                break;
            case CollaborateOptions.PROSPECTS:
                userPreferencesCopy.collaborateProspects
                    = !userPreferencesCopy.collaborateProspects;
                break;
            default:
                break;
        }

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    };

    private allCollaborateOptionsOn = (): boolean => {
        return (
            this.props.userPreferences.collaborateAOIThematics
            && this.props.userPreferences.collaborateLocationFilters
            && this.props.userPreferences.collaboratePushpins
            && this.props.userPreferences.collaborateReportHistory
            && this.props.userPreferences.collaborateTerritories
            && this.props.userPreferences.collaborateViewReports
        );
    };
    private atLeastOneCollaborateOptionOn = (): boolean => {
        return (
            this.props.userPreferences.collaborateAOIThematics
            || this.props.userPreferences.collaborateLocationFilters
            || this.props.userPreferences.collaboratePushpins
            || this.props.userPreferences.collaborateReportHistory
            || this.props.userPreferences.collaborateTerritories
            || this.props.userPreferences.collaborateViewReports
        );
    };

    private defaultAppChange = (app: string): void => {
        this.setState({
            defaultApp: app
        });

        const userPreferencesCopy: IUserPreferences = {
            ...this.props.userPreferences,
        };
        userPreferencesCopy.defaultLoginApp = app;

        this.setLoadingUserPreferences(true);
        this.props.updateUserPreferences(userPreferencesCopy);
    }
}

export default UserPreferences;
